import { type Result, ok, err } from 'neverthrow';
import { deserializeFn } from './functions';
import type {
  SerializedSchema,
  SerializedSchemaConfig,
  SerializedSchemaConfigOption,
  SerializedSchemaConfigSection,
  SerializedSchemaEntry,
  SerializedSchemaMap,
  SerializedSchemaNode,
} from './types';
import { serializedSchemaConfigSchema, serializedSchemaSchema } from './types';
import type {
  RuntimeSchema,
  RuntimeSchemaConfig,
  RuntimeSchemaConfigOption,
  RuntimeSchemaConfigSection,
  RuntimeSchemaEntry,
  RuntimeSchemaMap,
  RuntimeSchemaNode,
} from './types/runtime';
import { ParserError } from './error';
import { deserializeZod } from './zod';

const isSerializedSchemaEntry = (
  node: SerializedSchemaNode,
): node is SerializedSchemaEntry => {
  return 'validation' in node;
};

const deserializeSchemaEntry = (
  entry: SerializedSchemaEntry,
): Result<RuntimeSchemaEntry, ParserError> => {
  const {
    displayName,
    validation,
    type,
    allowedTypes,
    prefill,
    configKey,
    semantics,
    updateObject,
  } = entry;

  const hydrated = deserializeZod(validation);
  if (hydrated.isErr()) return err(hydrated.error);

  let hydratedPrefill: RuntimeSchemaEntry['prefill'];
  if (prefill) {
    const deserializedPrefill = deserializeFn(prefill);
    if (deserializedPrefill.isErr()) return err(deserializedPrefill.error);
    hydratedPrefill =
      deserializedPrefill.value as RuntimeSchemaEntry['prefill'];
  }

  return ok({
    displayName,
    validation: hydrated.value,
    type,
    allowedTypes,
    prefill: hydratedPrefill,
    configKey,
    semantics,
    updateObject,
  });
};

const deserializeSchemaNode = (
  node: SerializedSchemaNode,
): Result<RuntimeSchemaNode, ParserError> => {
  if (isSerializedSchemaEntry(node)) {
    return deserializeSchemaEntry(node);
  }

  return deserializeSchemaMap(node);
};

const deserializeSchemaMap = (
  schemaMap: SerializedSchemaMap,
): Result<RuntimeSchemaMap, ParserError> => {
  const { values, ...rest } = schemaMap;

  const hydratedValues: Record<string, RuntimeSchemaNode> = {};
  for (const [key, value] of Object.entries(values)) {
    const hydrated = deserializeSchemaNode(value);
    if (hydrated.isErr()) return err(hydrated.error);

    hydratedValues[key] = hydrated.value;
  }

  return ok({
    ...rest,
    values: hydratedValues,
  });
};

const deserializeConfigOption = (
  option: SerializedSchemaConfigOption,
): Result<RuntimeSchemaConfigOption, ParserError> => {
  const {
    type,
    title,
    description,
    input,
    selectionDeclareVariable,
    selection,
    default: defaultValue,
    placeholder,
    advanced,
    options,
    conditions,
  } = option;

  const hydrated = deserializeZod(type);
  if (hydrated.isErr()) return err(hydrated.error);

  type RuntimeSelectionItem = NonNullable<
    RuntimeSchemaConfigOption['selection']
  >[number];

  const hydratedSelection = selection?.map((item) => ({
    label: item.label as string,
    value: item.value as RuntimeSelectionItem['value'],
  }));

  return ok({
    title,
    description,
    type: hydrated.value,
    input,
    selectionDeclareVariable,
    selection: hydratedSelection,
    default: defaultValue,
    placeholder,
    advanced,
    options,
    conditions,
  });
};

const deserializeConfigSection = (
  section: SerializedSchemaConfigSection,
): Result<RuntimeSchemaConfigSection, ParserError> => {
  const {
    title,
    description,
    hidden,
    advanced,
    childConfigurations,
    options,
    conditions,
    mutate,
  } = section;

  const hydratedOptions: Record<string, RuntimeSchemaConfigOption> = {};
  for (const [key, option] of Object.entries(options)) {
    const hydrated = deserializeConfigOption(option);
    if (hydrated.isErr()) return err(hydrated.error);

    hydratedOptions[key] = hydrated.value;
  }

  const hydratedMutate = deserializeFn(mutate);
  if (hydratedMutate.isErr()) return err(hydratedMutate.error);

  return ok({
    title,
    description,
    hidden,
    advanced,
    childConfigurations,
    options: hydratedOptions,
    conditions,
    mutate: hydratedMutate.value as RuntimeSchemaConfigSection['mutate'],
  });
};

export const deserializeSchemaConfig = (
  data: unknown,
): Result<RuntimeSchemaConfig, ParserError> => {
  if (!data) return ok({});

  const parsed = serializedSchemaConfigSchema.safeParse(data);
  if (!parsed.success) return err(ParserError.ZodDeserializationError);

  const config: SerializedSchemaConfig = parsed.data;
  if (!config) return ok({});

  const hydratedSections: Record<string, RuntimeSchemaConfigSection> = {};
  for (const [key, section] of Object.entries(config)) {
    const hydrated = deserializeConfigSection(section);
    if (hydrated.isErr()) return err(hydrated.error);

    hydratedSections[key] = hydrated.value;
  }

  return ok(hydratedSections);
};

export const deserializeSchema = (
  data: unknown,
): Result<RuntimeSchema, ParserError> => {
  const parsed = serializedSchemaSchema.safeParse(data);
  if (!parsed.success) return err(ParserError.ZodDeserializationError);

  const serialized: SerializedSchema = parsed.data;

  const hydratedConfig = deserializeSchemaConfig(serialized.config);
  if (hydratedConfig.isErr()) return err(hydratedConfig.error);

  const hydratedDefinition = deserializeSchemaMap(serialized.definition);
  if (hydratedDefinition.isErr()) return err(hydratedDefinition.error);

  return ok({
    updateTemplateDiscardFields: [...serialized.updateTemplateDiscardFields],
    config: hydratedConfig.value,
    definition: hydratedDefinition.value,
  });
};
